import { createInput, createSelect, createTabs, MountTarget, newDialog } from './utils/elements';

export const TUSSENSTAP_EDIT_SELECTOR = '.cds-lrn-tussenstap';

type TussenstapStep = { type: string; value: string; isLastIntermediate?: boolean };

export function tussenstapDialog(
  insertHtml: (html: string) => void,
  mountTarget?: MountTarget,
  existingEl?: HTMLElement
) {
  let config: TussenstapStep[] = [];
  const existingSteps: TussenstapStep[] = existingEl ? readConfig(existingEl) : [];

  const snapshot = (list: { title: string; content: any }[]): TussenstapStep[] =>
    list.map(({ content }, index) => {
      if (content.value && !content.block) {
        return { type: 'base', value: content.value() };
      }
      return {
        type: content.blockType?.value(),
        value: content.newTabField?.value(),
        isLastIntermediate: index === list.length - 2,
      };
    });

  const listenToTabs = (list: { title: string; content: any }[]) => {
    const listenToInput = () => {
      config = snapshot(list);
      dialog.querySelector('#result').innerHTML = createIntermediateTable(config).outerHTML;
    };

    list.forEach(({ content }) => {
      const field: HTMLElement = content.block ?? content.field ?? content;

      field.removeEventListener('input', listenToInput);
      field.addEventListener('input', listenToInput);
    });
  };

  const createBlock = (index) => {
    const block = document.createElement('div');

    const blockType = createSelect('blockType', 'Type', [
      { label: 'Gewone tussenstap', value: 'intermediate' },
      { label: 'Splitsbenen', value: 'split' },
      { label: 'Splitsbenen met stopstreep', value: 'split-stop' },
      { label: 'Splitsbenen met tussenstap', value: 'split-intermediate' },
    ]);

    const newTabField = createInput(`part${index + 1}`, `Tussenstap ${index + 1}`, 'text', `bv. + 3`);

    block.append(blockType.field, newTabField.field);
    return { block, blockType, newTabField };
  };

  const dialog = newDialog('Tussenstap maken', () => insertHtml(''), mountTarget);

  const base = createInput('base', 'Basis', 'text', 'bv. 7 + 8 = 7');
  const end = createInput('end', 'Einde', 'text', 'bv. + 5 = 15');

  // Reconstruct intermediate blocks from existingSteps (skip first=base and last=end)
  const intermediateConfigs = existingSteps.slice(1, -1);
  const baseStep = existingSteps[0];
  const endStep = existingSteps[existingSteps.length - 1];
  if (baseStep?.value) base.setValue(baseStep.value);
  if (endStep?.value) end.setValue(endStep.value);

  const intermediateBlocks =
    intermediateConfigs.length > 0
      ? intermediateConfigs.map((step, idx) => {
          const blk = createBlock(idx);
          if (step.type) blk.blockType.setValue(step.type);
          if (step.value) blk.newTabField.setValue(step.value);
          return blk;
        })
      : [createBlock(0)];

  const tabs = [
    { title: 'Start', content: base },
    ...intermediateBlocks.map((blk, idx) => ({ title: `Tussenstap ${idx + 1}`, content: blk })),
    { title: 'Einde', content: end },
  ];

  const { tabWrapper, addButton, addTab } = createTabs(tabs, true);
  addButton.addEventListener('click', () => {
    const newTabIndex = tabs.length - 2;
    const newTabTitle = `Tussenstap ${newTabIndex + 1}`;
    const newTabField = createBlock(newTabIndex);
    const tab = { title: newTabTitle, content: newTabField };

    tabs.splice(tabs.length - 1, 0, tab);

    addTab(tab, 2);
    listenToTabs(tabs);
  });

  listenToTabs(tabs);
  config = snapshot(tabs);

  const resultEl = document.createElement('div');
  resultEl.classList.add('p-m', 'surface', 'body-small');
  resultEl.id = 'result';
  resultEl.innerHTML = '';
  dialog.querySelector('#cds-dialog-content').append(tabWrapper, resultEl);

  dialog.addEventListener('onSubmit', () => {
    insertHtml(createIntermediateTable(config).outerHTML);
  });
}

function readConfig(el: HTMLElement): TussenstapStep[] {
  try {
    return JSON.parse(el.getAttribute('data-cb-config') || '[]');
  } catch {
    return [];
  }
}

function createIntermediateTable(config: TussenstapStep[]) {
  const table = document.createElement('table');
  table.style.borderSpacing = '0';
  table.classList.add('cds-lrn-tussenstap', 'lrn_width_auto');
  table.setAttribute('data-cb-config', JSON.stringify(config));

  const tbody = document.createElement('tbody');
  table.append(tbody);

  const rows = {
    value: document.createElement('tr'),
    split: document.createElement('tr'),
    intermediate: document.createElement('tr'),
    splittedResult: document.createElement('tr'),
  };

  let shouldAddSplitRow = false;
  let shouldAddSplittedResultRow = false;

  config.forEach((config) => {
    if (config.value === '') return;
    switch (config.type) {
      case 'base':
        createBaseCells(config.value, rows);
        break;
      case 'intermediate':
        createBaseIntermediateCells(config.value, rows);
        break;
      case 'split':
        createSplitIntermediateCells(config.value, rows);
        shouldAddSplitRow = true;
        break;
      case 'split-stop':
        createSplitIntermediateCells(config.value, rows, true);
        shouldAddSplitRow = true;
        break;
      case 'split-intermediate':
        createSplitIntermediateCells(config.value, rows, false, true);
        shouldAddSplitRow = true;
        shouldAddSplittedResultRow = true;
        break;
    }
  });

  tbody.append(rows.value);

  if (shouldAddSplitRow) tbody.append(rows.split);

  tbody.append(rows.intermediate);

  if (shouldAddSplittedResultRow) tbody.append(rows.splittedResult);

  return table;
}

function createBaseCells(value: string, rows) {
  const valueCell = createTableCell(value);

  rows.value.append(valueCell);
  rows.split.append(createEmptyCell());
  rows.intermediate.append(createEmptyCell());
  rows.splittedResult.append(createEmptyCell());
}

function createBaseIntermediateCells(value: string, rows) {
  const valueCell = createTableCell(value, true);

  rows.value.append(valueCell);
  rows.intermediate.append(createTableCell('x', true, false, 'right'));
  rows.split.append(createEmptyCell(true));
  rows.splittedResult.append(createEmptyCell());
}

function createEmptyCell(border = false) {
  const cell = document.createElement('td');

  if (border) cell.classList.add('border-ie', 'border-2');

  return cell;
}

function createSplitIntermediateCells(value: string, rows, splitAndStop = false, splitAndIntermediate = false) {
  const valueCell = createTableCell(value, false, splitAndStop || splitAndIntermediate, 'center');
  valueCell.classList.add('-m-i-3xs');
  valueCell.colSpan = 2;
  rows.value.append(valueCell);

  const leftSplitCell = createSplitCell(splitAndIntermediate);
  const rightSplitCell = createSplitCell(splitAndStop || splitAndIntermediate, false);
  rows.split.append(leftSplitCell, rightSplitCell);

  const intermediateResultLeft = createTableCell('x', false, splitAndIntermediate, 'center');
  const intermediateResultRight = createTableCell('x', false, splitAndStop || splitAndIntermediate, 'center');
  rows.intermediate.append(intermediateResultLeft, intermediateResultRight);

  const splittedResultCellLeft = splitAndIntermediate
    ? createTableCell('x', false, splitAndIntermediate, 'center')
    : document.createElement('td');
  const splittedResultCellRight = splitAndIntermediate
    ? createTableCell('x', false, false, 'center')
    : document.createElement('td');
  rows.splittedResult.append(splittedResultCellLeft, splittedResultCellRight);
}

function createTableCell(value: string, addBorder = false, addIntermediateBorder = false, align = 'left') {
  const cell = document.createElement('td');

  cell.classList.add('p-i-3xs', 'white-space-nowrap');

  if (addBorder) cell.classList.add('border-ie', 'border-2');
  if (addIntermediateBorder) cell.classList.add('border-ie', 'border-2', 'text-center');
  if (align) cell.classList.add(`text-${align}`);

  cell.append(value);

  return cell;
}

function createSplitCell(addBorder = false, isLeft = true) {
  const cell = document.createElement('td');
  cell.style.verticalAlign = 'top';
  if (addBorder) cell.classList.add('border-ie', 'border-2');

  const x1 = isLeft ? '100%' : '0%';
  const x2 = isLeft ? '50%' : '50%';

  cell.innerHTML = `
    <div class="relative" style="min-width: 24px; height: 16px;">
      <svg width="100%" height="100%" class="absolute inline inset-0">
        <line x1="${x1}" x2="${x2}" y1="0%" y2="100%" class="stroke-stroke" style="stroke-width: 2px;"></line>
      </svg>
    </div>`;

  return cell;
}
